"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, BookOpen, Calendar, Clock, Download, FileText, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
interface ReportItem {
  id: string;
  title: string;
  category: string;
  publishedDate: string;
  readTime: string;
  pages: string;
  abstract: string;
  coverImage: string;
  coverSrcset: string;
  coverSizes: string;
  href: string;
}
const reportsData: ReportItem[] = [{
  id: "rep-1",
  title: "2026 State of B2B Demand Generation",
  category: "Executive Research",
  publishedDate: "January 2026",
  readTime: "24 min read",
  pages: "58 pages",
  abstract: "An empirical investigation across 640 B2B revenue teams examining budget reallocations, pipeline velocity shifts, and the transition from volume acquisition to account-level precision.",
  coverImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/report_cover_demand_gen.png",
  coverSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/report_cover_demand_gen.png-wvc-srcset",
  coverSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/report_cover_demand_gen.png-wvc-sizes",
  href: "/ResearchReportsPage"
}, {
  id: "rep-2",
  title: "AI in Revenue Operations: 2026 Outlook",
  category: "Industry Benchmark",
  publishedDate: "February 2026",
  readTime: "18 min read",
  pages: "44 pages",
  abstract: "Field analysis measuring how enterprise RevOps groups implement autonomous forecasting workflows, signal orchestration, and automated deal inspection models.",
  coverImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/report_cover_ai_revops.png",
  coverSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/report_cover_ai_revops.png-wvc-srcset",
  coverSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/report_cover_ai_revops.png-wvc-sizes",
  href: "/ResearchReportsPage"
}, {
  id: "rep-3",
  title: "2026 B2B Marketing Trends",
  category: "Market Analysis",
  publishedDate: "January 2026",
  readTime: "22 min read",
  pages: "52 pages",
  abstract: "Comprehensive survey of brand positioning, content distribution economics, and executive engagement models across European and North American enterprise sectors.",
  coverImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/report_cover_marketing_trends.png",
  coverSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/report_cover_marketing_trends.png-wvc-srcset",
  coverSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/report_cover_marketing_trends.png-wvc-sizes",
  href: "/ResearchReportsPage"
}, {
  id: "rep-4",
  title: "Future of Enterprise Technology",
  category: "Strategic Briefing",
  publishedDate: "December 2025",
  readTime: "30 min read",
  pages: "64 pages",
  abstract: "A rigorous assessment of infrastructure consolidation, security architectures, and core software lifecycle management for CIOs and CTOs navigating modern cloud costs.",
  coverImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/report_cover_enterprise_tech.png",
  coverSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/report_cover_enterprise_tech.png-wvc-srcset",
  coverSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/report_cover_enterprise_tech.png-wvc-sizes",
  href: "/ResearchReportsPage"
}, {
  id: "rep-5",
  title: "Healthcare Technology Market Outlook",
  category: "Sector Report",
  publishedDate: "November 2025",
  readTime: "26 min read",
  pages: "60 pages",
  abstract: "Market dynamics, clinical data governance standards, and investment forecasts for healthtech systems modernizing patient outcomes and operational telemetry.",
  coverImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/report_cover_healthcare_tech.png",
  coverSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/report_cover_healthcare_tech.png-wvc-srcset",
  coverSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/report_cover_healthcare_tech.png-wvc-sizes",
  href: "/ResearchReportsPage"
}];
const containerVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.08
    }
  }
};
const cardVariants = {
  hidden: {
    opacity: 0,
    y: 16
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.16, 1, 0.3, 1]
    }
  }
};
export default function Reports() {
  return <section id="reports-list" data-nav="light" className="bg-background py-16 sm:py-20 lg:py-24 border-b border-border text-foreground">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
        {/* Masthead Header Rule */}
        <div className="mb-12 sm:mb-16">
          <div className="flex items-center justify-between gap-4 pb-4 border-b-2 border-primary">
            <div className="flex items-center gap-2">
              <BookOpen className="size-4 text-primary" />
              <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                MinfoMedia Research Library
              </span>
            </div>
            <span className="font-mono text-xs text-muted-foreground hidden sm:inline-block">
              ISSN 2841-9214 • Open Editorial Access
            </span>
          </div>
          <div className="pt-2 border-b border-border flex flex-col md:flex-row md:items-end justify-between gap-4 pb-6">
            <div className="max-w-2xl">
              <h2 className="text-3xl sm:text-4xl font-serif font-semibold tracking-tight text-foreground">
                Flagship research reports
              </h2>
              <p className="mt-3 text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
                Empirical benchmarks, data-driven sector analyses, and verified executive surveys published by MinfoMedia analysts.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-mono text-xs bg-secondary text-secondary-foreground px-3 py-1.5 rounded-sm">
                Showing 5 primary studies
              </span>
            </div>
          </div>
        </div>

        {/* Reports Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {reportsData.map((report, i) => <motion.div key={report.id} data-index={i} variants={cardVariants} whileHover={{
          y: -4
        }} className="h-full flex">
              <Card className="flex flex-col justify-between w-full rounded-md border border-border bg-card p-0 shadow-xs hover:shadow-md transition-[border-color,box-shadow] duration-250">
                {/* Visual Publication Spine Header */}
                <div className="relative aspect-[16/10] overflow-hidden bg-primary rounded-t-md border-b border-border flex flex-col justify-between p-6 text-primary-foreground">
                  {/* Subtle spine gradient overlay */}
                  <div className="absolute inset-y-0 left-0 w-3 bg-gradient-to-r from-black/30 via-white/10 to-transparent pointer-events-none" />
                  
                  <div className="flex items-center justify-between z-10">
                    <span className="font-mono text-[11px] bg-primary-foreground/15 text-primary-foreground px-2 py-0.5 rounded-sm border border-primary-foreground/20">
                      {report.category}
                    </span>
                    <Sparkles className="size-3.5 text-primary-foreground/60" />
                  </div>

                  <div className="z-10 mt-auto">
                    <p className="font-mono text-[10px] tracking-wider uppercase text-primary-foreground/70 mb-1">
                      MinfoMedia Enterprise Study
                    </p>
                    <h3 className="font-serif text-lg sm:text-xl font-semibold leading-snug line-clamp-2 text-primary-foreground">
                      {report.title}
                    </h3>
                  </div>
                </div>

                <CardContent className="flex flex-col flex-1 p-6">
                  {/* Meta Strip */}
                  <div className="flex flex-wrap items-center gap-y-2 gap-x-4 text-xs text-muted-foreground font-mono pb-4 border-b border-border/70 mb-4">
                    <div className="flex items-center gap-1.5">
                      <SafeHydrate><Calendar className="size-3.5" /></SafeHydrate>
                      <span>{report.publishedDate}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Clock className="size-3.5" />
                      <span>{report.readTime}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <FileText className="size-3.5" />
                      <span>{report.pages}</span>
                    </div>
                  </div>

                  {/* Title & Abstract */}
                  <div className="flex-1 mb-6">
                    <h4 className="font-serif text-xl font-semibold text-card-foreground mb-3 leading-snug hover:text-primary transition-colors">
                      <Link to={report.href} className="hover:underline">
                        {report.title}
                      </Link>
                    </h4>
                    <p className="text-sm text-muted-foreground font-sans leading-relaxed line-clamp-3">
                      {report.abstract}
                    </p>
                  </div>

                  {/* Action Row */}
                  <div className="pt-4 border-t border-border flex items-center justify-between gap-3 mt-auto">
                    <Button asChild variant="default" size="sm" className="rounded-sm font-sans font-medium px-4 h-9 shadow-xs">
                      <Link to={report.href} className="inline-flex items-center gap-1.5">
                        <span>Read report</span>
                        <ArrowUpRight className="size-3.5" />
                      </Link>
                    </Button>

                    <Button asChild variant="ghost" size="sm" className="h-9 px-2 text-xs font-mono text-muted-foreground hover:text-foreground">
                      <Link to={report.href} className="inline-flex items-center gap-1">
                        <Download className="size-3" />
                        <span>PDF</span>
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Editorial Standards Footnote Strip */}
        <div className="mt-14 pt-8 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <span className="size-2 rounded-full bg-primary" />
            <span className="font-sans">
              All MinfoMedia publications undergo independent editorial and data verification before release.
            </span>
          </div>
          <Link to="/AboutUsPage" className="font-mono hover:text-foreground inline-flex items-center gap-1 transition-colors">
            Review publishing standards
            <ArrowUpRight className="size-3" />
          </Link>
        </div>
      </div>
    </section>;
}